// src/components/ui/lazyImage/LazyImage.tsx
'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './LazyImage.module.scss';
import Image from 'next/image';

type Aspect = number | `${number}/${number}` | 'auto';

interface LazyImageProps {
  src: string;
  alt?: string;
  className?: string;
  imgClassName?: string;
  style?: React.CSSProperties;
  imgStyle?: React.CSSProperties;
  width?: number | string; // 容器寬
  height?: number | string; // 固定高（若不想用比例）
  aspect?: Aspect; // 比例；給一個就能維持版面穩定
  radius?: number | string; // 外框圓角
  rootMargin?: string;
  threshold?: number;
  skeletonRadius?: number | string;
  onLoad?: () => void;
  sizes?: string;

  /**
   * 當 aspect="auto" 且 width="auto" 時，
   * 圖片載入前會先用這個比例當占位，避免版面大跳動。
   * 預設 4/3（可自行調整成 1/1 或 16/9）
   */
  autoAspectFallback?: number | `${number}/${number}`;
}

const parseAspect = (aspect: Aspect | number | `${number}/${number}` | undefined) => {
  if (!aspect || aspect === 'auto') return null;
  if (typeof aspect === 'number') return aspect;
  const [w, h] = String(aspect).split('/').map(Number);
  if (!w || !h) return null;
  return w / h;
};

const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt = '',
  className,
  imgClassName,
  style,
  imgStyle,
  width = '100%',
  height,
  aspect = 'auto',
  radius = 8,
  rootMargin = '200px',
  threshold = 0.01,
  onLoad,
  sizes,
  autoAspectFallback = '4/3',
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // ✅ for aspect="auto"：實際載入後計算出來的比例
  const [measuredRatio, setMeasuredRatio] = useState<number | null>(null);

  const isAutoWidth = width === 'auto' || width === 'fit-content';

  const ratioFromProp = useMemo(() => parseAspect(aspect), [aspect]);
  const fallbackRatio = useMemo(() => parseAspect(autoAspectFallback), [autoAspectFallback]);

  // 最終 ratio：
  // 1) 有指定 aspect（非 auto）就用它
  // 2) aspect=auto 就用 measuredRatio
  const ratio = ratioFromProp ?? measuredRatio;

  // 在「width auto + height 固定」時才能靠 aspect-ratio 推出寬度
  const canUseAspectRatio = !!height && isAutoWidth;

  // 如果 ratio 還沒量到，但我們需要一個穩定的寬度，就先用 fallbackRatio
  const ratioForLayout = ratio ?? (aspect === 'auto' && canUseAspectRatio ? fallbackRatio : null);

  // paddingTop 只用在「沒有固定高度」時（你的原本邏輯）
  const paddingTop = ratioFromProp && !height ? `${100 / ratioFromProp}%` : undefined;

  useEffect(() => {
    if (shouldLoad) return;

    const el = wrapperRef.current;
    if (!el) return;

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first && first.isIntersecting) {
          setShouldLoad(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin, threshold }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin, threshold, shouldLoad]);

  // src 改變時要重置狀態（尤其輪播/列表重用時）
  useEffect(() => {
    setLoaded(false);
    setMeasuredRatio(null);
  }, [src]);

  return (
    <div
      ref={wrapperRef}
      className={`${styles.wrapper} ${className || ''}`}
      style={{
        width,
        height,
        borderRadius: radius,
        position: 'relative',
        overflow: 'hidden',

        // ✅ SwiperSlide 才能 width auto
        display: isAutoWidth ? 'inline-block' : undefined,

        // ✅ height 固定 + width auto：用比例推寬度（混合比例也可以）
        aspectRatio: canUseAspectRatio && ratioForLayout ? String(ratioForLayout) : undefined,

        ...style,
      }}
    >
      {/* 比例占位：只在「沒有固定高度」的情況 */}
      {paddingTop && <div style={{ paddingTop }} />}

      {shouldLoad && (
        <Image
          className={`${styles.img} ${loaded ? styles.loaded : ''} ${imgClassName || ''}`}
          src={src}
          alt={alt}
          fill
          loading="lazy"
          sizes={sizes}
          style={imgStyle}
          onLoad={(e) => {
            // ✅ 用 naturalWidth/Height 量到正確比例（只有 aspect="auto" 才需要）
            if (aspect === 'auto') {
              const img = e.currentTarget as HTMLImageElement;
              const w = img.naturalWidth;
              const h = img.naturalHeight;
              if (w > 0 && h > 0) {
                const r = w / h;
                // 避免極端值把版面搞爆（可自行調）
                if (Number.isFinite(r) && r > 0.2 && r < 5) {
                  setMeasuredRatio(r);
                }
              }
            }

            setLoaded(true);
            onLoad?.();
          }}
        />
      )}

      {!loaded && <div className={styles.skeletonOverlay} />}
    </div>
  );
};

export default LazyImage;
